import React, { useEffect, useState } from 'react';
import { Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { opsApi } from '../../services/api';
import { AiCosts as AiCostsData, AiPriceTable } from '../../types';
import { count, featureLabel, money, shortMonth } from '../../utils/costs';
import AmountBars from './AmountBars';
import { CostPanel, Figure, Figures } from './CostPanel';

/** Paid model calls: tailored resumes first, then every call by feature and model, and the price table behind the numbers. */
const AiCosts: React.FC<{ ai: AiCostsData }> = ({ ai }) => {
  const [prices, setPrices] = useState<AiPriceTable | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    opsApi
      .aiPrices(controller.signal)
      .then(setPrices)
      .catch(() => {
        // The price table is reference material; the page works without it.
      });
    return () => controller.abort();
  }, []);

  const resumes = ai.tailored_resumes;
  const month = ai.this_month;
  return (
    <>
      <CostPanel
        title="Tailored resumes"
        description="Built is what costs money (one build per resume, per job). Sent counts applications that went out with a tailored resume attached."
      >
        <Figures label="Tailored resumes">
          <Figure label="Built this month" value={count(resumes.generated.this_month)} hint={`${count(resumes.generated.today)} today, ${count(resumes.generated.all_time)} in all`} />
          <Figure label="Sent this month" value={count(resumes.submitted.this_month)} hint={`${count(resumes.submitted.today)} today, ${count(resumes.submitted.all_time)} in all`} />
          <Figure label="Model cost this month" value={money(resumes.cost_this_month)} hint={`${money(resumes.cost_all_time)} in all`} />
          <Figure
            label="Cost per resume"
            value={resumes.cost_per_resume === null ? 'None built yet' : money(resumes.cost_per_resume)}
            hint="average of every resume built"
          />
        </Figures>
      </CostPanel>

      <CostPanel title="All model calls this month" description="Every paid call is recorded with its token counts and the price at the time.">
        <Figures label="Model calls this month">
          <Figure label="Cost" value={money(month.cost)} hint={ai.on_aws_bill > 0 ? `${money(ai.on_aws_bill)} of it is on the AWS bill` : undefined} />
          <Figure label="Calls" value={count(month.calls)} />
          <Figure label="Input tokens" value={count(month.input_tokens)} hint={month.cached_input_tokens ? `${count(month.cached_input_tokens)} from cache` : undefined} />
          <Figure label="Output tokens" value={count(month.output_tokens)} />
        </Figures>
      </CostPanel>

      <CostPanel title="By feature">
        <AmountBars
          label="Model cost by feature this month"
          rows={ai.by_feature.map((row) => ({ label: featureLabel(row.name), amount: row.cost, detail: `${money(row.cost)} · ${count(row.calls)} calls` }))}
          format={(amount) => money(amount)}
          empty="No model call has been made this month."
        />
      </CostPanel>

      <CostPanel title="By model">
        <AmountBars
          label="Model cost by model this month"
          rows={ai.by_model.map((row) => ({ label: row.name, amount: row.cost, detail: `${money(row.cost)} · ${count(row.calls)} calls` }))}
          format={(amount) => money(amount)}
          empty="No model call has been made this month."
        />
      </CostPanel>

      {ai.months.length > 0 && (
        <CostPanel title="By month">
          <AmountBars
            label="Model cost by month"
            rows={ai.months.map((row) => ({ label: shortMonth(row.month), amount: row.cost, detail: `${money(row.cost)} · ${count(row.calls)} calls` }))}
            format={(amount) => money(amount)}
          />
        </CostPanel>
      )}

      {prices && prices.models.length > 0 && (
        <CostPanel
          title="Price table"
          description={`List prices in US dollars per million tokens${prices.as_of ? `, checked ${prices.as_of}` : ''}. A call's cost is worked out from this table when the call is made.`}
        >
          <TableContainer>
            <Table size="small" aria-label="Model prices" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Vendor</TableCell>
                  <TableCell>Model</TableCell>
                  <TableCell align="right">Input</TableCell>
                  <TableCell align="right">Cached input</TableCell>
                  <TableCell align="right">Output</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {prices.models.map((row) => (
                  <TableRow key={`${row.vendor}:${row.model}`}>
                    <TableCell>{row.vendor}</TableCell>
                    <TableCell>
                      {row.model}
                      {row.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {row.note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.input)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{row.cached_input === null ? 'None' : money(row.cached_input)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.output)}</TableCell>
                    <TableCell>
                      {row.source && /^https:\/\//.test(row.source) ? (
                        <Link href={row.source} target="_blank" rel="noopener noreferrer">
                          pricing page
                        </Link>
                      ) : (
                        row.source ?? ''
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CostPanel>
      )}
    </>
  );
};

export default AiCosts;
